import { SxProps, Theme } from '@mui/material/styles';

import AvatarDisplay from './avatar-display';
import EntityAvatarEditable from './entity-avatar-editable';

// ----------------------------------------------------------------------

export interface EntityAvatarProps {
  src?: string | null;
  /** Used for the initials fallback and image alt text. */
  name?: string | null;
  /** Avatar diameter in pixels. */
  size?: number;
  variant?: 'circular' | 'rounded' | 'square';
  /** Show the camera affordance + crop/upload flow. */
  editable?: boolean;
  /** JPEG for photos (default); PNG preserves logo transparency. */
  outputFormat?: 'jpeg' | 'png';
  /** Icon shown when there is no image and no usable initials (e.g. 'solar:buildings-bold'). */
  fallbackIcon?: string;
  /** Crop dialog title. */
  cropTitle?: string;
  /**
   * Called after the user uploads a new image (fileId + its public URL) or removes it (both null).
   * The parent persists the change (e.g. profile_image_id / company_logo_id).
   */
  onChange?: (fileId: string | null, publicUrl: string | null) => void | Promise<void>;
  sx?: SxProps<Theme>;
}

/**
 * Shared avatar for the candidate photo and company logo (JT1-1539, spec items 21/28). Renders the
 * image or an initials/icon placeholder; when `editable`, adds a camera affordance and a client-side
 * square crop (~1MB, JPG/PNG). Non-editable rendering is hook-free (see AvatarDisplay), so list rows
 * need no Redux provider — the upload machinery only loads when the avatar is editable.
 */
export default function EntityAvatar({
  editable = false,
  size,
  variant,
  fallbackIcon,
  src,
  name,
  sx,
  ...editableProps
}: EntityAvatarProps) {
  if (editable) {
    return (
      <EntityAvatarEditable
        src={src}
        name={name}
        size={size}
        variant={variant}
        fallbackIcon={fallbackIcon}
        sx={sx}
        {...editableProps}
      />
    );
  }

  return (
    <AvatarDisplay src={src} name={name} size={size} variant={variant} fallbackIcon={fallbackIcon} sx={sx} />
  );
}
